import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  getSelectedExhibitionId,
  getSelectedStep,
  getStepsValidity,
  selectOrderedStepValidities
} from '@app/app.reducer';
import { BaseRoute } from '@core/data-access/models/routes.model';
import { OrderRequest } from '@core/data-access/models/save-order/order-request.model';
import { Store } from '@ngrx/store';
import { ErrorHandlingService } from '@shared/error-handling/error-handling.service';
import { environment } from '@src/environments/environment';
import { HelperService } from '@store/helpers/helper.service';
import { StepName, StepNames, StepValidities } from '@store/step-forms/step.interface';
import { SetSelectedStep } from '@store/step-forms/steps-forms.actions';
import { getLastEnabledPage, getSortedVisibleStepValidities } from '@store/step-forms/utils/step-validities';
import { mapStepNameToStepRoute } from '@store/step-forms/utils/step-validity-mappers';
import { isEmpty, isNil } from 'lodash';
import { combineLatest, first } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StepsFormsService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly helperService = inject(HelperService);
  private readonly errorHandlingService = inject(ErrorHandlingService);

  sendFinalOrder(orderRequest: OrderRequest) {
    let selfReg = '';
    if (this.helperService.isSelfRegistration()) {
      selfReg = '?sr=true';
    }

    //clear all previously received errors from the API (so we don't interpret errors that occurred earlier in the shopping process):
    this.errorHandlingService.clearAllErrors();

    return this.http.post(`${environment.protocol}${environment.webApiUrl}/order/save-order${selfReg}`, orderRequest, {
      observe: 'response'
    });
  }

  navigateRelativeTo(navigateNumberOfSteps: number) {
    combineLatest({
      stepValidities: this.store.select(getStepsValidity),
      selectedStep: this.store.select(getSelectedStep),
      selectedExhibitionId: this.store.select(getSelectedExhibitionId)
    })
      .pipe(
        first(
          ({ stepValidities, selectedStep, selectedExhibitionId }) =>
            !isEmpty(stepValidities) && !!selectedStep && !isNil(selectedExhibitionId)
        )
      )
      .subscribe(({ stepValidities, selectedStep, selectedExhibitionId }) => {
        const visibleSteps = getSortedVisibleStepValidities(stepValidities) as StepNames;

        const indexOfStep = visibleSteps.indexOf(selectedStep);

        if (
          (navigateNumberOfSteps < 0 && indexOfStep > 0) ||
          (navigateNumberOfSteps > 0 && indexOfStep < visibleSteps.length - 1)
        ) {
          this.checkAndRedirectToNextStep(
            stepValidities,
            visibleSteps,
            selectedExhibitionId,
            indexOfStep,
            navigateNumberOfSteps,
            selectedStep
          );
        }
      });
  }

  checkAndRedirectToNextStep(
    stepValidities: StepValidities,
    visibleSteps: StepNames,
    selectedExhibitionId: number,
    indexOfStep: number,
    navigateNumberOfSteps: number,
    selectedStep: string
  ) {
    const baseRoute = this.helperService.isSelfRegistration() ? BaseRoute.SelfRegistration : BaseRoute.WebShop;

    let navigateTo = visibleSteps[indexOfStep + navigateNumberOfSteps];

    if (navigateTo === StepName.Payment) {
      console.log(
        '%c currentStep ',
        'background: #5aac56; color: #fff',
        `/${baseRoute}/${selectedExhibitionId}/${selectedStep}`
      );
      console.log(
        navigateNumberOfSteps === -1 ? '%c "back button" URL ' : '%c "continue button" URL ',
        'background: #d46a6a; color: #fff',
        `/${baseRoute}/${selectedExhibitionId}/${navigateTo}`
      );
    }

    if (!stepValidities[navigateTo].showInStepNavigation) {
      const ticketStep = visibleSteps.find((step) => step === StepName.Tickets);

      navigateTo = stepValidities[ticketStep].showInStepNavigation
        ? ticketStep
        : stepValidities[visibleSteps[0]].showInStepNavigation
          ? visibleSteps[0]
          : StepName.Tickets;

      console.log(
        '%c re-navigated URL ',
        'background: #6ba099; color: #fff',
        `/${baseRoute}/${selectedExhibitionId}/${navigateTo}`
      );
    }

    this.store.dispatch(new SetSelectedStep(navigateTo));

    this.router.navigate([`/${baseRoute}/${selectedExhibitionId}/${mapStepNameToStepRoute(navigateTo)}`]);
  }

  navigateToLastEnabledPage() {
    combineLatest({
      orderedStepValidities: this.store.select(selectOrderedStepValidities),
      selectedExhibitionId: this.store.select(getSelectedExhibitionId)
    })
      .pipe(first())
      .subscribe(({ orderedStepValidities, selectedExhibitionId }) => {
        const lastEnabledPage = getLastEnabledPage(orderedStepValidities);

        this.router.navigate([`/${BaseRoute.WebShop}/${selectedExhibitionId}/${lastEnabledPage.key}`]);
      });
  }
}
